import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { incidents } from '@cwos/api-client';
import { Pill, SkeletonSet, SuccessEmpty, TableCard, type Column } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { LoadFailure } from '../LoadFailure.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsField, SettingsHeader } from '../settings/common.js';
import {
  CATEGORY_READING,
  SEVERITY_READING,
  STATUS_READING,
  eventReading,
  followUpReading,
  formatWhen,
  isEditable,
  messageOf,
  notificationReading,
  participantSentence,
} from './incidentsLogic.js';

/**
 * One incident: what was reported, what the reporter answered, what has been
 * done, and the history that records it.
 *
 * A 404 HERE MEANS EITHER "NOT YOURS" OR "NOT THERE", and the server will not
 * say which — the message is a constant and the two paths are indistinguishable
 * by construction (`incident-reach.ts`). So the failure copy names both worlds
 * and picks neither. Reading it as "this was deleted" would be a guess, and the
 * guess a reader would act on.
 *
 * A CLOSED INCIDENT IS TERMINAL FROM THIS SCREEN. Every write refuses one with
 * a 409, and this domain exposes no reopen route, so the actions are withdrawn
 * rather than offered-and-refused.
 */
export function IncidentDetail() {
  const params = useParams({ strict: false }) as { incidentId?: string };
  const incidentId = params.incidentId ?? '';
  const qc = useQueryClient();
  const navigate = useNav();
  const permissions = usePermissions();
  /**
   * FOUR ANSWERS, NOT TWO. The note below names a permission the reader is
   * missing; `has()` is equally `false` when the set could not be re-read. The
   * route is gated on `incidents:read`, a DIFFERENT key, so that state reaches
   * this screen with a cached set that answers for the route and not for this.
   *
   * `canManage` stays default-deny for the four controls it gates.
   */
  const manageGate = authorityGate(permissions, incidents.INCIDENTS_MANAGE);
  const canManage = gateOpen(manageGate);

  const [openForm, setOpenForm] = useState<'follow-up' | 'resolve' | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [resolution, setResolution] = useState('');
  const [closeIt, setCloseIt] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['incidents', 'detail', incidentId],
    queryFn: () => incidents.detail(incidentId),
    enabled: incidentId !== '',
  });

  function reset() {
    setOpenForm(null);
    setTitle('');
    setDescription('');
    setResolution('');
    setCloseIt(false);
    setFormError(null);
  }

  const advance = useMutation({
    mutationFn: (status: incidents.IncidentStatus) => incidents.manage(incidentId, { status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['incidents'] }),
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const followUp = useMutation({
    mutationFn: () =>
      incidents.raiseFollowUp(incidentId, {
        title: title.trim(),
        ...(description.trim() === '' ? {} : { description: description.trim() }),
      }),
    onSuccess: () => {
      reset();
      void qc.invalidateQueries({ queryKey: ['incidents'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const resolveIt = useMutation({
    mutationFn: () =>
      incidents.resolve(incidentId, { resolution: resolution.trim(), close: closeIt }),
    onSuccess: () => {
      reset();
      void qc.invalidateQueries({ queryKey: ['incidents'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  if (detail.isLoading) {
    return (
      <div className="rn-col" style={{ maxWidth: 960 }}>
        <SkeletonSet surface="table" rows={6} />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="rn-col" style={{ maxWidth: 720 }}>
        <LoadFailure
          error={detail.error}
          message="We couldn’t open this incident just now. It may also be one your account does not reach, or one that is not there — the server answers those two identically on purpose, so that an id cannot be used to find out what has happened in this organization."
          onRetry={() => void detail.refetch()}
          mainRead
        />
      </div>
    );
  }

  const { incident, response, answers, history } = detail.data;
  const severity = SEVERITY_READING[incident.severity];
  const status = STATUS_READING[incident.status];
  const notified = notificationReading(incident);
  const follow = followUpReading(incident);
  const editable = isEditable(incident);

  const answerColumns: Column<incidents.IncidentDetail['answers'][number]>[] = [
    { key: 'label', header: 'Question', render: (a) => a.label },
    {
      key: 'value',
      header: 'Answer',
      render: (a) => {
        // NULL IS AN ANSWER OF ITS OWN. A question that was shown and left blank
        // is a different fact from one that was never shown, and the response
        // carries only the answers that were recorded — so a null here means
        // "asked, not answered" and must not read as an empty string.
        if (a.value === null) return <span className="rn-muted">Left blank</span>;
        if (Array.isArray(a.value)) return a.value.join(', ');
        if (typeof a.value === 'boolean') return a.value ? 'Yes' : 'No';
        return String(a.value);
      },
    },
    {
      key: 'attachment',
      header: 'Attachment',
      render: (a) => (a.attachmentRef === null ? '—' : a.attachmentRef),
    },
  ];

  return (
    <div className="rn-col" style={{ maxWidth: 1000 }}>
      <SettingsHeader
        title={`${CATEGORY_READING[incident.category]} · ${formatWhen(incident.occurredAt)}`}
        blurb={`Filed by ${incident.reportedBy.displayName} on ${formatWhen(incident.reportedAt)} · about ${participantSentence(incident)}`}
      />

      <div>
        <button
          type="button"
          className="rn-btn-quiet"
          onClick={() => navigate('/compliance/incidents')}
        >
          {'‹'} All incidents
        </button>
      </div>

      <SettingsCard>
        <div className="cmp-control-head">
          <h2 className="rn-card-title">Where this stands</h2>
          <span className="rn-row" style={{ gap: 'var(--space-8)' }}>
            <Pill variant={severity.variant} dot>
              {severity.label}
            </Pill>
            <Pill variant={status.variant} dot>
              {status.label}
            </Pill>
          </span>
        </div>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          {status.meaning}
        </p>

        <h3 className="rn-card-title" style={{ marginTop: 'var(--space-16)' }}>
          What was done at the time
        </h3>
        <p>{incident.immediateAction}</p>

        <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
          {notified.kind === 'notified'
            ? `Management was notified: ${notified.who}, ${formatWhen(notified.when)}.`
            : 'Management has NOT been notified. That is a recorded fact about this incident, not a gap in the display.'}
        </p>
        <p className="rn-muted">
          {follow.kind === 'raised'
            ? 'A follow-up task has been raised for this incident. It lives in the Tasks queue — an incident does not keep a queue of its own.'
            : follow.kind === 'closed'
              ? 'No follow-up was raised, and the incident is closed.'
              : 'No follow-up has been raised. One incident gets one follow-up, so raising it is a decision rather than a formality.'}
        </p>
        {incident.resolution !== null ? (
          <>
            <h3 className="rn-card-title" style={{ marginTop: 'var(--space-16)' }}>
              Resolution
            </h3>
            <p>{incident.resolution}</p>
            <p className="rn-muted">
              {incident.resolvedBy?.displayName ?? 'Somebody'} · {formatWhen(incident.resolvedAt)}
              {incident.closedAt === null
                ? ' · still open, so it can still be edited'
                : ` · closed ${formatWhen(incident.closedAt)}`}
            </p>
          </>
        ) : null}

        {formError ? (
          <p role="alert" style={{ color: 'var(--color-urgent-deep)' }}>
            {formError}
          </p>
        ) : null}

        {canManage && editable ? (
          <div
            className="rn-row"
            style={{ gap: 'var(--space-8)', marginTop: 'var(--space-16)', flexWrap: 'wrap' }}
          >
            {incident.status === 'reported' ? (
              <button
                type="button"
                className="rn-btn-quiet"
                disabled={advance.isPending}
                onClick={() => advance.mutate('under_review')}
              >
                Take it up
              </button>
            ) : null}
            {incident.status === 'under_review' ? (
              <button
                type="button"
                className="rn-btn-quiet"
                disabled={advance.isPending}
                onClick={() => advance.mutate('action_taken')}
              >
                Record that action was taken
              </button>
            ) : null}
            {follow.kind === 'none' ? (
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => {
                  setOpenForm('follow-up');
                  setFormError(null);
                }}
              >
                Raise a follow-up
              </button>
            ) : null}
            <button
              type="button"
              className="rn-btn"
              onClick={() => {
                setOpenForm('resolve');
                setFormError(null);
              }}
            >
              {incident.resolution === null ? 'Write the resolution' : 'Revise the resolution'}
            </button>
          </div>
        ) : null}

        {canManage && !editable ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
            This incident is closed, so it can no longer be edited — and there is no reopen route in
            this product. Nothing has been hidden from you.
          </p>
        ) : null}

        <GateNotice
          gate={manageGate}
          what="take this incident up, raise a follow-up or write a resolution"
          onRetry={() => permissions.refetch()}
          refusedNode={
            <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
              Taking this up, raising a follow-up or writing a resolution needs the{' '}
              {incidents.INCIDENTS_MANAGE} permission.
            </p>
          }
        />

        {openForm === 'follow-up' && canManage ? (
          <form
            className="rn-col"
            style={{ gap: 'var(--space-12)', marginTop: 'var(--space-16)' }}
            onSubmit={(e) => {
              e.preventDefault();
              if (title.trim() !== '') followUp.mutate();
            }}
          >
            <SettingsField
              label="What needs doing"
              hint="Becomes a task in the ordinary queue. One incident gets one follow-up, and it cannot be replaced."
            >
              <input value={title} onChange={(e) => setTitle(e.currentTarget.value)} />
            </SettingsField>
            <SettingsField label="Detail (optional)">
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
              />
            </SettingsField>
            <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
              <button
                type="submit"
                className="rn-btn"
                disabled={followUp.isPending || title.trim() === ''}
              >
                Raise it
              </button>
              <button type="button" className="rn-btn-quiet" onClick={reset}>
                Cancel
              </button>
            </div>
            {/* The assignee field is deliberately absent. The server refuses an
                assignee who cannot see what the incident is about, and offering
                a picker here would mean offering names the refusal would then
                reject — a disclosure by trial and error. Assigning is done from
                the task once it exists. */}
            <p className="rn-muted">
              It is raised to you. Reassigning happens on the task itself, where the check that the
              new owner can see what this is about is made against the person you choose.
            </p>
          </form>
        ) : null}

        {openForm === 'resolve' && canManage ? (
          <form
            className="rn-col"
            style={{ gap: 'var(--space-12)', marginTop: 'var(--space-16)' }}
            onSubmit={(e) => {
              e.preventDefault();
              if (resolution.trim() !== '') resolveIt.mutate();
            }}
          >
            <SettingsField
              label="What was done, and why that is enough"
              hint="Kept with the incident. This is what an auditor reads."
            >
              <textarea
                rows={4}
                value={resolution}
                onChange={(e) => setResolution(e.currentTarget.value)}
              />
            </SettingsField>
            <label className="rn-row" style={{ gap: 'var(--space-8)', alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={closeIt}
                onChange={(e) => setCloseIt(e.currentTarget.checked)}
              />
              <span>
                Close it as well.
                <br />
                <span className="rn-muted">
                  A closed incident can never be edited again, and this product has no reopen.
                  Leaving it open records the resolution and keeps the record editable.
                </span>
              </span>
            </label>
            <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
              <button
                type="submit"
                className="rn-btn"
                disabled={resolveIt.isPending || resolution.trim() === ''}
              >
                {closeIt ? 'Resolve and close' : 'Record the resolution'}
              </button>
              <button type="button" className="rn-btn-quiet" onClick={reset}>
                Cancel
              </button>
            </div>
          </form>
        ) : null}
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">What was reported</h2>
        <p className="rn-muted">
          Answers to version {response.versionNumber} of the incident form, as it was published when
          this was filed. The version is frozen: revising the form later cannot change what this
          report asked or what it says.
        </p>
        {answers.length === 0 ? (
          <SuccessEmpty
            heading="No answers recorded"
            message="This report carries no answers beyond the immediate action above."
          />
        ) : (
          <TableCard
            columns={answerColumns}
            rows={[...answers]}
            rowKey={(a) => a.id}
            caption="Reported answers"
          />
        )}
        {response.attestationStatement !== null ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
            Attested by {response.attestedBy?.displayName ?? 'the reporter'} ·{' '}
            {formatWhen(response.attestedAt)}: “{response.attestationStatement}”
          </p>
        ) : null}
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">History</h2>
        {history.length === 0 ? (
          <SuccessEmpty
            heading="Nothing recorded"
            message="No events are recorded against this incident."
          />
        ) : (
          <ol
            className="rn-col"
            style={{ gap: 'var(--space-12)', paddingLeft: 0, listStyle: 'none' }}
          >
            {[...history].map((event) => (
              <li key={event.id} style={{ display: 'grid', gap: 'var(--space-4)' }}>
                <span>
                  <strong>{eventReading(event.kind)}</strong>
                  {event.fromStatus && event.toStatus
                    ? ` · ${event.fromStatus} → ${event.toStatus}`
                    : null}
                </span>
                <span className="rn-muted">
                  {formatWhen(event.occurredAt)} ·{' '}
                  {event.actorKind === 'user'
                    ? (event.actor?.displayName ?? 'A person whose name we could not resolve')
                    : event.actorKind === 'phibian'
                      ? 'Phibian'
                      : 'The system'}
                </span>
                {event.detail ? <span className="rn-muted">{event.detail}</span> : null}
              </li>
            ))}
          </ol>
        )}
      </SettingsCard>
    </div>
  );
}
